
import React from "react";

const ProductCustomerProblems = ({
    data = [],
    onChange
}) => {

    const problems = Array.isArray(data)
        ? data
        : [];

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";


    // ========================================
    // PROBLEM MANAGEMENT
    // ========================================

    const addProblem = () => {

        onChange([
            ...problems,
            {
                problem: "",
                symptoms: [],
                possibleCauses: [],
                keywords: []
            }
        ]);

    };


    const removeProblem = (index) => {

        onChange(
            problems.filter((_, i) => i !== index)
        );

    };


    const updateProblem = (index, field, value) => {

        onChange(
            problems.map((item, i) =>
                i === index
                    ? {
                        ...item,
                        [field]: value
                    }
                    : item
            )
        );

    };


    // ========================================
    // REPEATABLE TEXT ITEMS
    // ========================================

    const addListItem = (problemIndex, field) => {

        const problem = problems[problemIndex];

        updateProblem(
            problemIndex,
            field,
            [
                ...(problem[field] || []),
                ""
            ]
        );

    };


    const updateListItem = (
        problemIndex,
        field,
        itemIndex,
        value
    ) => {

        const currentItems =
            problems[problemIndex][field] || [];

        updateProblem(
            problemIndex,
            field,
            currentItems.map((item, index) =>
                index === itemIndex
                    ? value
                    : item
            )
        );

    };


    const removeListItem = (
        problemIndex,
        field,
        itemIndex
    ) => {

        const currentItems =
            problems[problemIndex][field] || [];

        updateProblem(
            problemIndex,
            field,
            currentItems.filter(
                (_, index) => index !== itemIndex
            )
        );

    };


    // ========================================
    // REUSABLE LIST EDITOR
    // ========================================

    const renderListEditor = (
        problem,
        problemIndex,
        field,
        title,
        placeholder
    ) => {

        const items = problem[field] || [];

        return (

            <div className="space-y-3">

                <div className="flex justify-between items-center gap-3">

                    <label className={labelClass}>
                        {title}
                    </label>

                    <button
                        type="button"
                        onClick={() =>
                            addListItem(problemIndex, field)
                        }
                        className="text-sm text-blue-700 hover:underline"
                    >
                        + Add
                    </button>

                </div>

                {items.length === 0 && (

                    <p className="text-xs text-gray-500">
                        No entries added.
                    </p>

                )}

                {items.map((item, itemIndex) => (

                    <div
                        key={itemIndex}
                        className="flex gap-2"
                    >

                        <input
                            type="text"
                            value={item}
                            onChange={(event) =>
                                updateListItem(
                                    problemIndex,
                                    field,
                                    itemIndex,
                                    event.target.value
                                )
                            }
                            placeholder={placeholder}
                            className={inputClass}
                        />

                        <button
                            type="button"
                            onClick={() =>
                                removeListItem(
                                    problemIndex,
                                    field,
                                    itemIndex
                                )
                            }
                            className={removeButtonClass}
                        >
                            Remove
                        </button>

                    </div>

                ))}

            </div>

        );

    };


    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-6">

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b pb-4">

                <div>

                    <h3 className="text-base font-semibold text-gray-900">
                        Customer Problems
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                        Define the customer problems this product
                        can help address.
                    </p>

                </div>

                <button
                    type="button"
                    onClick={addProblem}
                    className={addButtonClass}
                >
                    + Add Problem
                </button>

            </div>


            {problems.length === 0 && (

                <div className="py-12 text-center border border-dashed rounded-lg">

                    <p className="text-gray-500 text-sm">
                        No customer problems added yet.
                    </p>

                    <p className="text-gray-400 text-xs mt-2">
                        Add problems that customers commonly
                        describe when looking for a solution.
                    </p>

                </div>

            )}


            {problems.map((problem, problemIndex) => (

                <div
                    key={problemIndex}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                >

                    {/* PROBLEM HEADER */}

                    <div className="flex justify-between items-center gap-3 bg-gray-100 px-5 py-4">

                        <div>

                            <h4 className="font-semibold text-gray-900">

                                Problem {problemIndex + 1}

                            </h4>

                            {problem.problem && (

                                <p className="text-xs text-gray-500 mt-1">
                                    {problem.problem}
                                </p>

                            )}

                        </div>

                        <button
                            type="button"
                            onClick={() =>
                                removeProblem(problemIndex)
                            }
                            className={removeButtonClass}
                        >
                            Remove Problem
                        </button>

                    </div>


                    {/* PROBLEM FIELDS */}

                    <div className="p-5 space-y-7">

                        <div>

                            <label className={labelClass}>
                                Customer Problem
                            </label>

                            <textarea
                                value={problem.problem || ""}
                                onChange={(event) =>
                                    updateProblem(
                                        problemIndex,
                                        "problem",
                                        event.target.value
                                    )
                                }
                                placeholder="e.g. Water seepage through concrete roof slabs during monsoon"
                                rows={3}
                                className={inputClass}
                            />

                        </div>


                        {renderListEditor(
                            problem,
                            problemIndex,
                            "symptoms",
                            "Symptoms",
                            "e.g. Damp patches appearing on ceiling"
                        )}


                        {renderListEditor(
                            problem,
                            problemIndex,
                            "possibleCauses",
                            "Possible Causes",
                            "e.g. Cracks in roof slab"
                        )}


                        {renderListEditor(
                            problem,
                            problemIndex,
                            "keywords",
                            "Customer Search Keywords",
                            "e.g. roof leakage"
                        )}

                    </div>

                </div>

            ))}

        </div>

    );

};

export default ProductCustomerProblems;
